
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { useUserRole } from './useUserRole';
import { usePublicCountry } from '@/contexts/PublicCountryContext';
import { setPublicCountryIdForApi } from '@/utils/publicCountryStore';
import { toast } from '@/components/ui/sonner';
import {
  isLiberiaApiEnabled,
  listLiberiaUserNotifications,
  markLiberiaNotificationAsRead,
  respondToLiberiaProjectInvitation,
} from '@/utils/liberiaApi';

const INVITATION_FALLBACK_TTL_DAYS = 14;
export const USER_NOTIFICATIONS_QUERY_KEY = 'user-notifications';

interface Notification {
  id: string;
  notification_type: string;
  title: string;
  message: string;
  data: any;
  is_read: boolean;
  created_at: string;
  source?: 'liberia';
}

async function loadUserNotifications(
  user: { id: string } | null,
  role: string | null,
  countryId: string | null
) {
  if (!user || !isLiberiaApiEnabled()) {
    return { notifications: [] as Notification[], unreadCount: 0 };
  }

  // Header is checked before the query. Keep them the same for this request.
  setPublicCountryIdForApi(countryId);
  const backendNotifications = await listLiberiaUserNotifications(
    { limit: 100, countryId },
    {
      user,
      role,
      hasAddressGenerationAccess: true,
    }
  ).catch((error) => {
    console.error('Error fetching QLS notifications:', error);
    return [];
  });

  const data = (backendNotifications || [])
    .map((notification) => ({
      ...notification,
      data: notification.data,
      source: 'liberia' as const,
    }))
    .sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

  const now = new Date();
  const expiredInvitationIds: string[] = [];

  data.forEach((notification) => {
    if (
      notification.notification_type === 'project_invitation' &&
      !notification.is_read &&
      (notification.data?.expires_at || notification.created_at)
    ) {
      const createdAt = new Date(notification.created_at);
      const fallbackExpiresAt = new Date(
        createdAt.getTime() + INVITATION_FALLBACK_TTL_DAYS * 24 * 60 * 60 * 1000
      );
      const expiresAt = notification.data?.expires_at
        ? new Date(notification.data.expires_at)
        : fallbackExpiresAt;

      if (expiresAt < now) {
        expiredInvitationIds.push(notification.id);
      }
    }
  });

  if (expiredInvitationIds.length > 0) {
    const liberiaIds = data
      .filter((notification) => expiredInvitationIds.includes(notification.id))
      .map((notification) => notification.id);

    if (liberiaIds.length > 0) {
      await Promise.all(
        liberiaIds.map((id) =>
          markLiberiaNotificationAsRead(id, {
            user,
            role,
            hasAddressGenerationAccess: true,
          }).catch((error) => {
            console.error('Error auto-marking QLS notification as read:', error);
          })
        )
      );
    }
  }

  const filteredData = data
    .filter((n) => !expiredInvitationIds.includes(n.id))
    .filter((n) => n.notification_type !== 'project_invitation' || !n.is_read);

  return {
    notifications: filteredData,
    unreadCount: filteredData.filter((n) => !n.is_read).length,
  };
}

export const useNotifications = () => {
  const { user } = useAuth();
  const { role } = useUserRole();
  const { countryId, resolving } = usePublicCountry();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [USER_NOTIFICATIONS_QUERY_KEY, user?.id, role, countryId ?? 'all'],
    queryFn: () => loadUserNotifications(user, role, countryId),
    enabled: Boolean(user) && isLiberiaApiEnabled() && !resolving,
    staleTime: 0,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const notifications = query.data?.notifications ?? [];
  const unreadCount = query.data?.unreadCount ?? 0;

  const fetchNotifications = async () => {
    await queryClient.invalidateQueries({ queryKey: [USER_NOTIFICATIONS_QUERY_KEY] });
  };

  const markAsRead = async (notificationId: string) => {
    try {
      if (!user || !isLiberiaApiEnabled()) return;

      await markLiberiaNotificationAsRead(notificationId, {
        user,
        role,
        hasAddressGenerationAccess: true,
      });

      await fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };

  const handleInvitationResponse = async (
    invitationId: string,
    response: 'accepted' | 'declined',
    notificationId?: string
  ) => {
    try {
      console.log('[notifications] invitation response:', { invitationId, response, notificationId });
      let success = false;

      if (isLiberiaApiEnabled() && user) {
        await respondToLiberiaProjectInvitation(invitationId, response, {
          user,
          role,
          hasAddressGenerationAccess: true,
        });
        success = true;
      }

      if (success) {
        toast.success(`Invitation ${response} successfully`);
        if (notificationId) {
          await markAsRead(notificationId);
        }
        await fetchNotifications();
        return true;
      }

      toast.error('Invalid or expired invitation');
      if (notificationId) {
        await markAsRead(notificationId);
      }
      await fetchNotifications();
      return false;
    } catch (error) {
      console.error('Error handling invitation response:', error);
      toast.error(`Failed to ${response} invitation`);

      if (response === 'declined' && notificationId) {
        await markAsRead(notificationId);
        await fetchNotifications();
      }
      return false;
    }
  };

  return {
    notifications,
    unreadCount,
    loading: query.isLoading,
    fetchNotifications,
    markAsRead,
    handleInvitationResponse
  };
};
